<template>
  <Navbar></Navbar>
  <div :class="['grid', 'm-2', darkMode ? 'dark-mode' : 'light-mode']">
    <div v-for="app in apps" :key="app.title" class="col-12 md:col-6 lg:col-3">
      <PCard>
        <template #header>
          <img :alt="app.title" :src="app.image" class="tile-thumbnail" />
        </template>
        <template #title>
          <div class="text-center">{{ app.title }}</div>
        </template>

        <template #content>
          <p class="m-0 text-center">{{ app.content }}</p>
        </template>

        <template #footer>
          <div class="flex gap-4 mt-1">
            <Button label="Open" class="w-full" as="a" :href="app.url" />
          </div>
        </template>
      </PCard>
    </div>
    <div class="col-12 md:col-6 lg:col-3">
      <PCard>
        <template #header>
          <img
            alt="Netsuite account"
            src="/assets/netsuiteaccount.png"
            class="tile-thumbnail"
          />
        </template>
        <template #title>
          <div class="text-center">Netsuite account</div>
        </template>

        <template #content>
          <p class="m-0 text-center">
            Allocate an account to login into NetSuite
          </p>
        </template>

        <template #footer>
          <div class="flex gap-4 mt-1">
            <NetsuiteAccount />
          </div>
        </template>
      </PCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import NetsuiteAccount from "@/components/NetsuiteAccount.vue";
import Navbar from "@wovar/common/components/Navbar.vue";
import { useDarkModeManager } from "@wovar/common/dark-mode/store.js";

const { darkMode } = useDarkModeManager();

const apps = ref([
  {
    title: "Verzendcentrale",
    content: "Checking orders, print labels and send packages.",
    image: "/assets/shippingcenter.png",
    url: `${location.protocol}//vc.${location.host}`,
  },
  {
    title: "Box packing",
    content: "More boxes on the transport belt!",
    image: "/assets/boxpacking.png",
    url: `${location.protocol}//boxes.${location.host}`,
  },
  // {
  //   title: "Error Lane",
  //   content: "Errorlane and actions.",
  //   image: "/assets/errorlane.png",
  //   url: `${location.protocol}//errorlane.${location.host}`,
  // },
  {
    title: "Order Picking",
    content: "Manage order picking flow.",
    image: "/assets/orderpicking.png",
    url: `${location.protocol}//orderpicking.${location.host}`,
  },
  {
    title: "Mobile WMS",
    content:
      "Replenishment, inventory adjustment, receive items and bin transfer.",
    image: "/assets/mobilewms.png",
    url: `${location.protocol}//mobile.${location.host}`,
  },
  {
    title: "Label Printing",
    content: "Print labels via NetSuite integration.",
    image: "/assets/labelprinting.png",
    url: `${location.protocol}//labelprinting.${location.host}`,
  },
  // {
  //   title: "Multi Order Picking",
  //   content: "View Non Maas Assignments Screen.",
  //   image: "/assets/multiorderpicking.png",
  //   url: `${location.protocol}//multiorderpicking.${location.host}`,
  // },
  // {
  //   title: "Mobile Apps",
  //   content: "Mobile Apps collection.",
  //   image: "/assets/inventoryadjustment.png",
  //   url: `${location.protocol}//mobile.${location.host}`,
  // },
  {
    title: "Quick Pick & Ship",
    content: "Quick pick and ship orders efficiently",
    image: "/assets/quickpickship.png",
    url: `${location.protocol}//qps.${location.host}`,
  },
  {
    title: "User management",
    content: "Manage users and the apps they have access to.",
    image: "/assets/usermanagement.png",
    url: `${location.protocol}//users.${location.host}`,
  },
]);
</script>

<style scoped lang="css">
.card-image-container {
  height: 200px;
  width: 100%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tile-thumbnail {
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform: scale(0.95);
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

:deep(.p-card) {
  height: 470px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.p-card .p-card-header) {
  height: 300px;
  overflow: hidden;
  position: relative;
  background-color: #fff;
}

:deep(.p-card .p-card-body) {
  position: relative;
  height: 200px;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.p-card .p-card-content) {
  padding: 1.25rem;
  transition: background-color 0.3s ease;
}

:deep(.p-card .p-card-footer) {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  padding: 0 1.25rem;
  background: inherit;
}

:deep(.p-card:hover) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

:deep(.p-card:hover .p-card-header .tile-thumbnail) {
  transform: scale(1.05);
}

:deep(.p-card:hover .p-card-content) {
  background-color: rgba(0, 0, 0, 0.02);
}

:deep(.p-button) {
  text-decoration: none;
}

:deep(.p-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

:deep(.p-button:focus) {
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px var(--primary-color);
}

:deep(.p-button .p-button-icon) {
  font-size: 0.875rem;
  margin-left: 0.25rem;
}

.grid {
  padding: 1.5rem;
}

:deep(.p-card-title) {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

:deep(.p-card-content p) {
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.7);
}
</style>
